import React, { useCallback, useEffect, useState } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { FIELD_CONTROL_RADIUS, fieldControlStyle } from './field-control-style';

type NumberFieldProps = {
  field: { label?: string };
  name: string;
  /**
   * Optional because a sub-field of a Puck `object` field is read out of the field store *by path*,
   * which yields `undefined` when the parent object is not in the stored props -- the block-level
   * `defaultProps` merge does not reach inside an object field to supply it.
   *
   * Seeding every default layout (ECOMM-13011) is what stops that happening for data this codebase
   * authors, and is the actual fix for the crash that surfaced it. This stays optional because the
   * type would otherwise be false: unvalidated stored JSON can still omit the key, and the widget
   * has to render *some* number regardless. See `absentValue` for which number.
   */
  value?: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

type NumberInputWithUnitProps = {
  name: string;
  unit: string;
  value: number | string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  min: number;
  max?: number;
  step?: number;
  /** DOM id for the input, when the caller needs a stable hook (Puck passes one to custom fields). */
  id?: string;
  /** Accessible name, for when no visible label wraps the input. */
  ariaLabel?: string;
};

/** The shared look of every numeric inspector input: the number input with a grey unit badge. */
export function NumberInputWithUnit({
  name,
  unit,
  value,
  onChange,
  onBlur,
  readOnly,
  min,
  max,
  step = 1,
  id,
  ariaLabel,
}: NumberInputWithUnitProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input
        id={id}
        name={name}
        aria-label={ariaLabel}
        type='number'
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={readOnly}
        readOnly={readOnly}
        min={min}
        max={max}
        step={step}
        data-testid={`number-input-${name}`}
        style={{
          ...fieldControlStyle,
          flex: 1,
          // Without this the input's intrinsic width wins inside a narrow
          // container (e.g. an `object` field group) and clips the unit badge.
          minWidth: 0,
        }}
      />
      {unit && (
        <span
          data-testid={`number-unit-${name}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'stretch',
            padding: '0 12px',
            background: '#f0f2f4',
            borderRadius: FIELD_CONTROL_RADIUS,
            fontSize: 12,
            color: '#646d72',
            fontWeight: 500,
          }}
        >
          {unit}
        </span>
      )}
    </div>
  );
}

/**
 * Returns a Puck custom field render function for numeric CSS values.
 * The `unit` parameter (e.g. 'px', 'rem') is shown as a suffix badge
 * and appended to the stored value.
 *
 * `absentValue` is the number the input displays when the stored value is missing. It exists to keep
 * the panel *honest*, not to supply a default: it must equal whatever the renderer falls back to for
 * that same absent value, or the editor reports one number while the live page renders another --
 * the asymmetry `section.tsx`'s padding comment warns about.
 *
 * Defaults to `0${unit}`, already correct for every spacing caller (`spacingToCss` resolves an
 * absent box to `NO_SPACING`). `border.width` is the one exception: `borderStyle` falls back to
 * `DEFAULT_BORDER.width` (1px), so the two border callers pass it explicitly.
 *
 * Note this is display-only and is never written to the store, so opening a panel cannot persist a
 * value the author did not choose.
 */
export function NumberField(unit: string, absentValue = `0${unit}`, max?: number) {
  return function NumberFieldWidget({ field, name, value, onChange, readOnly }: NumberFieldProps): JSX.Element {
    const parsedValue = typeof value === 'string' ? parseFloat(value) : parseFloat(absentValue);
    const numericValue = Number.isFinite(parsedValue) ? Math.min(max ?? Infinity, Math.max(0, parsedValue)) : 0;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const number = e.currentTarget.valueAsNumber;
        const clamped = Number.isFinite(number) ? Math.min(max ?? Infinity, Math.max(0, number)) : 0;
        onChange(`${clamped}${unit}`);
      },
      [onChange]
    );

    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <NumberInputWithUnit
          name={name}
          unit={unit}
          value={numericValue}
          onChange={handleChange}
          readOnly={readOnly}
          min={0}
          max={max}
        />
      </FieldLabel>
    );
  };
}

type NumericFieldProps = {
  field: { label?: string };
  name: string;
  id?: string;
  value?: number;
  onChange: (value: number) => void;
  readOnly?: boolean;
};

/**
 * Like `NumberField` but the stored value stays a plain number (no unit string) bounded to
 * `[min, max]`. The input keeps its own draft text while it is edited, so it can be emptied and
 * retyped. Typing is not clamped mid-edit (typing "2" on the way to "28" must not snap to the
 * minimum); each valid number is saved as typed, an empty draft saves nothing and returns to the stored
 * value on blur, and the value is clamped when the input loses focus. Renderers clamp again on read.
 * A missing or malformed saved value shows `fallback`, which must equal the renderer's fallback.
 */
export function NumericField(
  unit: string,
  {
    min,
    max,
    step = 1,
    fallback = min,
    hideLabel = false,
  }: {
    min: number;
    max: number;
    step?: number;
    fallback?: number;
    /** Drop the visible label (the control sits under a toggle that already names it); the input keeps its accessible name. */
    hideLabel?: boolean;
  }
) {
  const clamp = (n: number): number => Math.min(max, Math.max(min, n));
  return function NumericFieldWidget({ field, name, id, value, onChange, readOnly }: NumericFieldProps): JSX.Element {
    const stored = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    // What the author is typing, while they type. `null` means "show the stored value". An empty or
    // half-typed draft must be allowed to exist, or backspacing the last digit snaps the old value
    // back and the next digits append to it (3 -> backspace -> 21 became 321).
    const [draft, setDraft] = useState<string | null>(null);

    // A stored value changed from outside (undo, redo, another control): drop a draft that no longer matches it.
    useEffect(() => {
      setDraft((current) => (current !== null && Number(current) !== stored ? null : current));
    }, [stored]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setDraft(e.currentTarget.value);
        const number = e.currentTarget.valueAsNumber;
        // Only a real number is saved; an emptied or invalid input saves nothing until blur.
        if (Number.isFinite(number)) onChange(number);
      },
      [onChange]
    );
    const handleBlur = useCallback(() => {
      // Untouched: judge the stored value, so an out-of-range saved number is still pulled into range.
      const text = draft ?? String(stored);
      const number = text.trim() === '' ? NaN : Number(text);
      // Empty or invalid: go back to the stored value. Otherwise commit the clamped number.
      if (Number.isFinite(number) && clamp(number) !== number) onChange(clamp(number));
      setDraft(null);
    }, [draft, stored, onChange]);
    const input = (
      <NumberInputWithUnit
        id={id}
        name={name}
        unit={unit}
        value={draft ?? stored}
        onChange={handleChange}
        onBlur={handleBlur}
        readOnly={readOnly}
        min={min}
        max={max}
        step={step}
        ariaLabel={hideLabel ? field.label ?? name : undefined}
      />
    );
    if (hideLabel) return input;
    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        {input}
      </FieldLabel>
    );
  };
}
